import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { PortalShell } from "@/components/AppHeader";
import { BookingPanel } from "@/pages/customer/BookingPanel";
import { ActiveTripPanel } from "@/pages/customer/ActiveTripPanel";
import { RentalPanel } from "@/pages/customer/RentalPanel";
import { api } from "@/lib/api";
import { AppBanner } from "@/components/AppBanner";
import { RatingPrompts } from "@/components/RatingPrompts";
import { AdSlot } from "@/components/AdSlot";
import { ZelleWaiting } from "@/components/PaymentMethodPicker";

export default function CustomerHome() {
  const [trip, setTrip] = useState(undefined);
  const [zelle, setZelle] = useState(null);
  const [mode, setMode] = useState("ride");
  const tripRef = useRef(null);
  tripRef.current = trip;

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/trips/active");
      setZelle(data.pending_zelle || null);
      if (data.trip) return setTrip(data.trip);
      const cur = tripRef.current;
      if (cur && !["completed", "cancelled"].includes(cur.status)) {
        const r = await api.get(`/trips/${cur.id}`);
        return setTrip(r.data);
      }
      if (cur === undefined) setTrip(null);
    } catch { /* keep last state */ }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 5000);
    window.addEventListener("lux-trip-refresh", poll);
    return () => { clearInterval(t); window.removeEventListener("lux-trip-refresh", poll); };
  }, [poll]);

  return (
    <PortalShell>
      <div data-testid="customer-dashboard">
        <AppBanner />
        <RatingPrompts />
        {trip === undefined && <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin text-amber-400" />}
        {trip === null && zelle && <ZelleWaiting trip={zelle} onChange={poll} />}
        {trip === null && !zelle && (
          <>
            <AdSlot placement="customer_dashboard" className="mb-6" />
            <div className="mb-6 inline-flex rounded-full border border-amber-500/20 bg-[#121216] p-1" data-testid="booking-mode-toggle">
              {[["ride", "Ride"], ["rental", "Rent by the Hour / Night"]].map(([k, l]) => (
                <button key={k} data-testid={`booking-mode-${k}`} onClick={() => setMode(k)}
                  className={`rounded-full px-4 py-2 text-sm transition-colors ${mode === k ? "bg-amber-400 text-black" : "text-zinc-300 hover:text-amber-200"}`}>{l}</button>
              ))}
            </div>
            {mode === "ride" ? <BookingPanel /> : <RentalPanel />}
          </>
        )}
        {trip && <ActiveTripPanel trip={trip} onUpdate={setTrip} onDone={() => setTrip(null)} />}
      </div>
    </PortalShell>
  );
}
